import { useState } from 'react';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { api, ApiError } from '@/lib/api';
import type { Job } from '@/types';

interface JobFailedAlertProps {
  job: Job;
  /** True when the current user may re-run the pipeline (admin / reviewer). */
  canRetry: boolean;
  /** Fired after a successful retry so the parent can re-fetch job state. */
  onRetried?: () => void;
}

/**
 * Failure-detail panel for a job in status='failed'.
 *
 * Renders the dispatcher's captured `jobs.error` (CF cancel, persist crash,
 * stage timeout etc.) and — for admins / reviewers — a "Retry pipeline"
 * button that hits POST /api/v1/jobs/{id}/stages/resume. The backend
 * recovery route flips the job from 'failed' back to 'processing', clears
 * the error, and dispatches the stage after the last approved one.
 */
export function JobFailedAlert({ job, canRetry, onRetried }: JobFailedAlertProps) {
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  async function handleRetry() {
    setIsRetrying(true);
    setRetryError(null);
    try {
      await api.post(`/api/v1/jobs/${job.id}/stages/resume`, {});
      onRetried?.();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Retry failed. Please try again.';
      setRetryError(msg);
      setIsRetrying(false);
    }
  }

  return (
    <Alert variant="destructive" className="mt-3">
      <AlertCircle className="h-4 w-4" aria-hidden="true" />
      <AlertTitle>Pipeline failed</AlertTitle>
      <AlertDescription className="space-y-2">
        {job.error ? (
          <p className="font-mono text-xs break-words whitespace-pre-wrap">{job.error}</p>
        ) : (
          <p className="text-xs">
            No diagnostic message was captured. Check Railway logs for the run.
          </p>
        )}
        {canRetry && (
          <div className="flex items-center gap-2 pt-1">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="gap-1.5 bg-background"
              onClick={() => { void handleRetry(); }}
              disabled={isRetrying}
            >
              {isRetrying ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {isRetrying ? 'Restarting…' : 'Retry pipeline'}
            </Button>
            <span className="text-xs text-muted-foreground">
              Re-runs from the last approved stage.
            </span>
          </div>
        )}
        {retryError && (
          <p role="alert" className="text-xs">{retryError}</p>
        )}
      </AlertDescription>
    </Alert>
  );
}
